import { uniqueNamesGenerator, adjectives, colors, animals, names } from 'unique-names-generator'
import {
  CompetitorMap,
  CompetitionRound,
  Competition,
  Heat,
  HeatResult,
  Competitor,
  CompetitionRoundEnum
} from '@/types'
import { getSurferRating } from '@/utils'
import { GamePlayData } from '@/types'
import { CompetitionEnum } from '@/types'
import LOCATIONS from '@/constants/locations'

export const getOrdinalSuffix = (n: number): string => {
  const j = n % 10
  const k = n % 100
  if (j === 1 && k !== 11) return 'st'
  if (j === 2 && k !== 12) return 'nd'
  if (j === 3 && k !== 13) return 'rd'
  return 'th'
}

export const generateCompetitorName = (): string => {
  let name = uniqueNamesGenerator({ dictionaries: [animals, adjectives], separator: ' ' })
  name = name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return name
}

export const generateCompetitors = (
  count: number,
  playerName: string,
  playerRating: number,
  avgCompetitorRating: number,
  specialCompetitors?: Competitor[]
): CompetitorMap => {
  const minRating = avgCompetitorRating * 0.7
  const maxRating = Math.min(avgCompetitorRating * 1.15, 10)

  // Calculate how many random competitors we need to generate
  // Total = count - 1 (player) - specialCompetitors.length
  const specialCount = specialCompetitors?.length || 0
  const randomCompetitorsCount = count - 1 - specialCount

  // Generate random competitors
  const competitors = Array.from({ length: randomCompetitorsCount }, () => ({
    name: generateCompetitorName(),
    rating: Math.floor(Math.random() * (maxRating - minRating)) + minRating
  }))

  // Add special competitors if provided
  if (specialCompetitors) {
    competitors.push(...specialCompetitors)
  }

  // Add player
  competitors.push({
    name: playerName,
    rating: playerRating
  })

  return competitors.reduce((acc, competitor) => {
    acc[competitor.name] = competitor
    return acc
  }, {} as CompetitorMap)
}

export const getSortedCompetitors = (competitors: CompetitorMap): string[] => {
  return Object.values(competitors)
    .sort((a, b) => b.rating - a.rating)
    .map((c) => c.name)
}

const distributeCompetitorsToHeats = (competitors: string[], heatCount: number): Heat[] => {
  const heats: Heat[] = Array.from({ length: heatCount }, (_, index) => ({
    id: index + 1,
    competitors: []
  }))

  // First pass: distribute competitors to heats (1st competitors)
  for (let i = 0; i < heatCount && i < competitors.length; i++) {
    heats[i].competitors.push(competitors[i])
  }

  // Second pass: distribute 2nd competitors
  for (let i = 0; i < heatCount && i + heatCount < competitors.length; i++) {
    heats[i].competitors.push(competitors[i + heatCount])
  }

  // Third pass: distribute 3rd competitors
  for (let i = 0; i < heatCount && i + heatCount * 2 < competitors.length; i++) {
    heats[i].competitors.push(competitors[i + heatCount * 2])
  }

  // Fourth pass: distribute 4th competitors
  for (let i = 0; i < heatCount && i + heatCount * 3 < competitors.length; i++) {
    heats[i].competitors.push(competitors[i + heatCount * 3])
  }

  return heats
}

export const calculatePrize = (totalPrize: number, position: number): number => {
  const prizeDistribution = [0.6, 0.2, 0.1]
  return totalPrize * (prizeDistribution[position - 1] || 0)
}

export const getLeaderboard = (competitorMap: CompetitorMap): Competitor[] => {
  return Object.values(competitorMap).sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
}

export const generateRounds = (competitors: string[]): CompetitionRound[] => {
  const rounds: CompetitionRound[] = []
  const totalCompetitors = competitors.length

  // Calculate regular rounds first (working down from total)
  let regularRoundSizes: number[] = []
  let remainingCompetitors = totalCompetitors

  while (remainingCompetitors > 8) {
    regularRoundSizes.push(remainingCompetitors)
    remainingCompetitors = Math.ceil(remainingCompetitors / 2) // Half progress to next round
  }

  // Combine with knockout rounds
  const roundSizes = [
    ...regularRoundSizes, // Regular rounds (if any)
    8, // Quarterfinals
    4, // Semifinals
    2 // Finals
  ]

  // Generate rounds from first to last
  roundSizes.forEach((targetCompetitorCount, index) => {
    const isRegularRound = targetCompetitorCount > 8
    const competitorsPerHeat = isRegularRound ? 4 : 2
    const heatCount = Math.ceil(targetCompetitorCount / competitorsPerHeat)

    let roundKey: string
    if (index === roundSizes.length - 1) {
      roundKey = 'finals'
    } else if (index === roundSizes.length - 2) {
      roundKey = 'semifinals'
    } else if (index === roundSizes.length - 3) {
      roundKey = 'quarterfinals'
    } else {
      roundKey = `round_${index + 1}`
    }

    // For first round, distribute actual competitors
    const roundCompetitors = index === 0 ? competitors : Array(targetCompetitorCount).fill('') // Placeholder for future competitors

    const heats = distributeCompetitorsToHeats(roundCompetitors, heatCount)

    rounds.push({
      key: roundKey as CompetitionRoundEnum,
      heats,
      isComplete: false
    })
  })

  return rounds
}

const isKnockoutStage = (roundKey: string): boolean => {
  return ['quarterfinals', 'semifinals', 'finals'].includes(roundKey)
}

export const getHeatWinners = (heatResults: HeatResult[] | undefined, roundKey: string): string[] => {
  if (!heatResults) return []
  // In knockout stages (quarters onwards), only first place advances
  const advancingPositions = isKnockoutStage(roundKey) ? 1 : 2
  return heatResults.filter((r) => r.position <= advancingPositions).map((r) => r.competitorName)
}

export const simulateHeat = (heat: Heat, competitorMap: CompetitorMap, roundKey: string): HeatResult[] => {
  // Sort competitors by rating and add some randomness

  const sortedCompetitors = heat.competitors
    .map((name) => ({
      name,
      finalRating: Math.min(competitorMap[name].rating * (0.8 + Math.random() * 0.4) * 2, 20) // Add 40% randomness and make max score 20
    }))
    .sort((a, b) => b.finalRating - a.finalRating)

  return sortedCompetitors.map((competitor) => ({
    competitorName: competitor.name,
    score: competitor.finalRating,
    position: sortedCompetitors.indexOf(competitor) + 1
  }))
}

export const getFinalWinners = (rounds: CompetitionRound[]): string[] => {
  const finalRound = rounds[rounds.length - 1]
  const winners: string[] = []

  // Get top 3 from final heat results
  if (finalRound.heats[0]?.results) {
    const finalHeatResults = finalRound.heats[0].results
    // Sort by position and take top 3
    const top3 = finalHeatResults
      .sort((a, b) => a.position - b.position)
      .slice(0, 3)
      .map((result) => result.competitorName)

    winners.push(...top3)
  }

  return winners
}

export const generateEnrollData = (gameData: GamePlayData, competitionId: CompetitionEnum): GamePlayData => {
  // Initial setup
  const currentLocation = LOCATIONS[gameData.currentLocation]
  const competition = currentLocation.competitions?.find((c) => c.key === competitionId)
  if (!competition) return gameData

  const playerRating = getSurferRating(gameData)
  const avgCompetitorRating = competition.avgCompetitorRating

  const generatedCompetitors = generateCompetitors(
    competition.maxCompetitors,
    gameData.username,
    playerRating,
    avgCompetitorRating,
    competition.specialCompetitors // Pass special competitors from competition
  )
  const sortedCompetitors = getSortedCompetitors(generatedCompetitors)
  const rounds = generateRounds(sortedCompetitors)

  // Create initial game state
  const initialGameData = {
    ...gameData,
    currencies: {
      ...gameData.currencies,
      money: {
        ...gameData.currencies.money,
        amount: gameData.currencies.money.amount - competition.entryFee
      }
    },
    currentCompetition: {
      key: competitionId,
      rounds,
      currentRoundIndex: 0,
      currentHeatIndex: 0,
      competitorMap: generatedCompetitors,
      sortedCompetitors,
      isComplete: false,
      currentPage: 1
    }
  }

  // Progress heats until player's first heat
  return progressHeatsUntilPlayerHeat(initialGameData as GamePlayData)
}

export const getStartHeatData = (gameData: GamePlayData): GamePlayData => {
  const gdcc = gameData.currentCompetition
  if (!gdcc) return gameData
  const competition = LOCATIONS[gameData.currentLocation].competitions?.find((c) => c.key === gdcc.key)

  // Simulate current player heat
  const currentHeat = gdcc.rounds[gdcc.currentRoundIndex].heats[gdcc.currentHeatIndex]
  let updatedRounds = [...gdcc.rounds]
  let updatedCompetitorMap = { ...gdcc.competitorMap }
  const currentRound = gdcc.rounds[gdcc.currentRoundIndex]
  let currentRoundIndex = gdcc.currentRoundIndex
  let currentHeatIndex = gdcc.currentHeatIndex
  const results = simulateHeat(currentHeat, gdcc.competitorMap, gdcc.rounds[gdcc.currentRoundIndex].key)
  updatedRounds[gdcc.currentRoundIndex].heats[gdcc.currentHeatIndex].results = results

  const isComplete = currentRoundIndex === updatedRounds.length - 1
  // If this is the last heat in the round, prepare next round
  if (currentHeatIndex === currentRound.heats.length - 1 && currentRoundIndex <= updatedRounds.length - 1) {
    updatedRounds = generateNextRound(updatedRounds, currentRoundIndex, gdcc.competitorMap)
    if (!isComplete) {
      currentRoundIndex++
      currentHeatIndex = 0
    }
  } else {
    currentHeatIndex++
  }

  // Update competitor map with results
  updatedCompetitorMap = processHeatResults(
    updatedCompetitorMap,
    results,
    currentRound.key,
    currentHeatIndex,
    isComplete,
    competition?.prize
  )

  // Update the game state with the results
  const updatedGameData = {
    ...gameData,
    currentCompetition: {
      ...gdcc,
      rounds: updatedRounds,
      currentHeatIndex,
      currentRoundIndex,
      competitorMap: updatedCompetitorMap,
      isComplete
    }
  }
  if (updatedCompetitorMap[gameData.username]?.prize) {
    updatedGameData.currencies.money.amount += updatedCompetitorMap[gameData.username].prize || 0
  }
  // Progress heats until next player heat or end
  return isComplete ? updateCompetitionStats(updatedGameData) : progressHeatsUntilPlayerHeat(updatedGameData)
}

export const progressHeatsUntilPlayerHeat = (gameData: GamePlayData): GamePlayData => {
  const gdcc = gameData.currentCompetition
  if (!gdcc) return gameData
  const competition = LOCATIONS[gameData.currentLocation].competitions?.find((c) => c.key === gdcc.key)
  let updatedRounds = [...gdcc.rounds]
  let updatedCompetitorMap = { ...gdcc.competitorMap }
  let currentRoundIndex = gdcc.currentRoundIndex
  let currentHeatIndex = gdcc.currentHeatIndex
  let isComplete = false

  while (currentRoundIndex < updatedRounds.length) {
    // Move this inside the while loop so it updates when currentRoundIndex changes
    let currentRound = updatedRounds[currentRoundIndex]

    while (currentHeatIndex < currentRound.heats.length) {
      const currentHeat = currentRound.heats[currentHeatIndex]

      // Check if this is player's heat
      if (currentHeat.competitors.includes(gameData.username)) {
        return {
          ...gameData,
          currentCompetition: {
            ...gdcc,
            rounds: updatedRounds,
            currentRoundIndex,
            currentHeatIndex,
            competitorMap: updatedCompetitorMap,
            isComplete
          }
        }
      }

      const results = simulateHeat(currentHeat, updatedCompetitorMap, currentRound.key)
      updatedRounds[currentRoundIndex].heats[currentHeatIndex].results = results

      // Update competitor map with results
      updatedCompetitorMap = processHeatResults(
        updatedCompetitorMap,
        results,
        currentRound.key,
        currentHeatIndex,
        currentRoundIndex === updatedRounds.length - 1,
        competition?.prize
      )

      // If this is the last heat in the round, prepare next round
      if (currentHeatIndex === currentRound.heats.length - 1 && currentRoundIndex < updatedRounds.length - 1) {
        updatedRounds = generateNextRound(updatedRounds, currentRoundIndex, updatedCompetitorMap)
        currentRoundIndex++
        currentHeatIndex = 0
        currentRound = updatedRounds[currentRoundIndex]
        continue
      }

      currentHeatIndex++
    }

    // If we've completed the final round, mark as complete
    if (currentRoundIndex === updatedRounds.length - 1) {
      isComplete = true
      break
    }

    currentRoundIndex++
    currentHeatIndex = 0
    // Update currentRound reference for the new round
    currentRound = updatedRounds[currentRoundIndex]
  }

  // Competition is complete
  return updateCompetitionStats({
    ...gameData,
    currentCompetition: {
      ...gdcc,
      rounds: updatedRounds,
      competitorMap: updatedCompetitorMap,
      isComplete: true
    }
  })
}

const processHeatResults = (
  competitorMap: CompetitorMap,
  results: HeatResult[],
  roundKey: string,
  heatIndex: number,
  isFinalRound: boolean = false,
  totalPrize?: number
): CompetitorMap => {
  let updatedMap = { ...competitorMap }
  if (isFinalRound) {
    results.forEach((result) => {
      updatedMap[result.competitorName] = {
        ...updatedMap[result.competitorName],
        position: result.position,
        prize: totalPrize ? calculatePrize(totalPrize, result.position) : undefined,
        eliminatedInRound: roundKey,
        eliminatedInHeat: heatIndex + 1
      }
    })
    return updatedMap
  }

  // Calculate competitors for this round based on round key
  let startingCompetitors: number
  let advancingCount: number

  if (roundKey === 'quarterfinals') {
    startingCompetitors = 8
    advancingCount = 4
  } else if (roundKey === 'semifinals') {
    startingCompetitors = 4
    advancingCount = 2
  } else {
    // Regular rounds (round_1, round_2, etc.)
    const roundNumber = parseInt(roundKey.replace('round_', ''))
    startingCompetitors = Object.keys(competitorMap).length / Math.pow(2, roundNumber - 1)
    advancingCount = startingCompetitors / 2
  }

  const advancingPositions = isKnockoutStage(roundKey) ? 1 : 2

  results.forEach((result) => {
    if (result.position > advancingPositions) {
      // Calculate total position based on round and position within heat
      let totalPosition
      if (result.position === 4) {
        totalPosition = startingCompetitors - Math.ceil(advancingCount / 2) + 1 // Last position among eliminated
      } else if (result.position === 3) {
        totalPosition = advancingCount + 1 // First position among eliminated
      } else if (result.position === 2) {
        totalPosition = advancingCount + 1 // Second position among in last rounds
      }

      updatedMap[result.competitorName] = {
        ...updatedMap[result.competitorName],
        position: totalPosition,
        eliminatedInRound: roundKey,
        eliminatedInHeat: heatIndex + 1,
        prize:
          totalPrize && result.position === 2 && roundKey === 'semifinals' ? calculatePrize(totalPrize, 3) : undefined
      }
    }
  })

  return updatedMap
}

const generateNextRound = (
  rounds: CompetitionRound[],
  currentRoundIndex: number,
  competitorMap: CompetitorMap
): CompetitionRound[] => {
  const updatedRounds = [...rounds]
  const nextRoundIndex = currentRoundIndex + 1

  if (nextRoundIndex >= rounds.length) return updatedRounds

  // Get all winners from the completed round
  const currentRound = rounds[currentRoundIndex]
  const roundWinners = currentRound.heats
    .filter((heat) => heat.results) // Only take heats with results
    .flatMap((heat) => getHeatWinners(heat.results!, currentRound.key))
    .sort((a, b) => competitorMap[b].rating - competitorMap[a].rating)

  // Distribute winners to next round's heats
  const nextRound = updatedRounds[nextRoundIndex]
  const heatCount = nextRound.heats.length
  updatedRounds[nextRoundIndex].heats = distributeCompetitorsToHeats(roundWinners, heatCount)

  return updatedRounds
}

export const updateCompetitionStats = (gameData: GamePlayData): GamePlayData => {
  const gdcc = gameData.currentCompetition
  if (!gdcc || !gdcc.isComplete) return gameData

  // Initialize stats for this competition if they don't exist
  if (!gameData.competitionStats[gdcc.key]) {
    gameData.competitionStats[gdcc.key] = {
      enrolled: 0,
      won: 0,
      finals: 0,
      semifinals: 0,
      quarterfinals: 0,
      round_4: 0,
      round_3: 0,
      round_2: 0,
      round_1: 0,
      prizeWon: 0,
      lastCompetitionGameDay: 0
    }
  }

  const stats = gameData.competitionStats[gdcc.key]!
  const playerResult = gdcc.competitorMap[gameData.username]

  // Update last competition day
  stats.lastCompetitionGameDay = Math.floor(gameData.gameMinutes / (60 * 24))

  // Increment enrolled count
  stats.enrolled++

  // Update prize money won
  if (playerResult.prize) {
    stats.prizeWon += playerResult.prize || 0
  }

  // Update round achievements based on elimination round or final position
  if (playerResult.position === 1) {
    stats.won++
  }

  if (!playerResult.eliminatedInRound) {
    return gameData // Safety check
  }

  // Update round statistics based on where player was eliminated or reached
  switch (playerResult.eliminatedInRound) {
    case 'finals':
      stats.finals++
      break
    case 'semifinals':
      stats.semifinals++
      break
    case 'quarterfinals':
      stats.quarterfinals++
      break
    case 'round_4':
      stats.round_4++
      break
    case 'round_3':
      stats.round_3++
      break
    case 'round_2':
      stats.round_2++
      break
    case 'round_1':
      stats.round_1++
      break
  }

  return {
    ...gameData,
    competitionStats: {
      ...gameData.competitionStats,
      [gdcc.key]: stats
    }
  }
}
